import React, { useEffect, useState } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import { Badge, Button, Container, Select, Text, Alert } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import dayjs from 'dayjs';

import { GET_RESTRICTED_SKU_IMPORT_LOGS } from '../../../apollo/queries/getRestrictedSkuImportLogs';
import { GetRestrictedSkuImportLogsQuery } from '../../../codegen/graphql';
import { ImportType } from '../../../types/import';
import When from '../../../components/common/When';
import { getErrorMessage } from '../../../helpers/getErrorMessage';
import ManualImportErrorCell from '../../../components/file-uploads/ManualImportErrorCell';
import RestrictedSkuFileCell from './RestrictedSkuFileCell';

const STATUS_COLORS: Record<string, string> = {
    Completed: 'green',
    Failed: 'red',
    Processing: 'blue',
    Pending: 'gray',
};

type RestrictedSkuImportLog =
    GetRestrictedSkuImportLogsQuery['getManualImportLogs']['logs'][number];

type RestrictedSkuUploadHistoryTableProps = {
    refreshKey: number;
};

function RestrictedSkuUploadHistoryTable({
    refreshKey,
}: RestrictedSkuUploadHistoryTableProps) {
    const [statusFilter, setStatusFilter] = useState<string | null>(null);

    const { data, error, refetch, networkStatus } =
        useQuery<GetRestrictedSkuImportLogsQuery>(
            GET_RESTRICTED_SKU_IMPORT_LOGS,
            {
                variables: {
                    input: {
                        importTypes: [ImportType.RestrictedSkuList],
                        statuses: statusFilter ? [statusFilter] : undefined,
                        limit: 50,
                        offset: 0,
                    },
                },
                fetchPolicy: 'cache-and-network',
            },
        );

    useEffect(() => {
        if (refreshKey === 0) {
            return;
        }
        refetch();
    }, [refreshKey, refetch]);

    const rows = data?.getManualImportLogs?.logs ?? [];

    return (
        <Container size="xl" className="rounded-lg p-4 mt-8 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <Text size="lg" fw={600}>
                    Upload history
                </Text>
                <Select
                    placeholder="All statuses"
                    clearable
                    data={[
                        { value: 'Completed', label: 'Completed' },
                        { value: 'Failed', label: 'Failed' },
                        { value: 'Processing', label: 'Processing' },
                        { value: 'Pending', label: 'Pending' },
                    ]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    className="w-48"
                />
            </div>
            <When condition={Boolean(error)}>
                <Alert color="red" variant="light" className="mb-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <Text size="sm">{getErrorMessage(error)}</Text>
                        <Button
                            size="xs"
                            variant="light"
                            onClick={() => refetch()}
                        >
                            Retry
                        </Button>
                    </div>
                </Alert>
            </When>
            <DataTable<RestrictedSkuImportLog>
                withTableBorder
                borderRadius="md"
                striped
                highlightOnHover
                minHeight={200}
                fetching={networkStatus === NetworkStatus.loading && !data}
                records={rows}
                idAccessor="id"
                noRecordsText={
                    error ? 'Unable to load uploads' : 'No uploads found'
                }
                columns={[
                    {
                        accessor: 'fileName',
                        title: 'File',
                        width: 280,
                        noWrap: true,
                        render: row => (
                            <RestrictedSkuFileCell
                                logId={row.id}
                                fileName={row.fileName}
                                s3Key={row.s3Key}
                            />
                        ),
                    },
                    {
                        accessor: 'status',
                        title: 'Status',
                        render: row => (
                            <Badge
                                color={STATUS_COLORS[row.status] || 'gray'}
                                variant="light"
                            >
                                {row.status}
                            </Badge>
                        ),
                    },
                    {
                        accessor: 'recordsProcessed',
                        title: 'Entries',
                        render: row => row.recordsProcessed ?? '-',
                    },
                    {
                        accessor: 'error',
                        title: 'Error',
                        width: 320,
                        render: row => (
                            <ManualImportErrorCell
                                logId={row.id}
                                status={row.status}
                            />
                        ),
                    },
                    {
                        accessor: 'createdBy',
                        title: 'Uploaded by',
                        render: row =>
                            row.createdBy
                                ? `${row.createdBy.firstName} ${row.createdBy.lastName}`
                                : '-',
                    },
                    {
                        accessor: 'completedAt',
                        title: 'Date',
                        render: row =>
                            dayjs(row.completedAt || row.createdAt).format(
                                'YYYY-MM-DD HH:mm',
                            ),
                    },
                ]}
            />
        </Container>
    );
}

export default RestrictedSkuUploadHistoryTable;
